import { TelemetryClient } from '../../TelemetryClient';
import type { TelemetryReportData } from '../../types';

import { CoreReportType, type CoreTelemetryReportData, type CoreTransmissionOptions } from './types';

export class CoreTelemetryClient extends TelemetryClient {
  private reported = new Set<string>();

  constructor(options: { appVersion: string; language: string }) {
    super();
    this.updateMeta(options);
  }

  report(reportData: Partial<TelemetryReportData>, options?: CoreTransmissionOptions) {
    if (options?.once && reportData.type && reportData.name) {
      let hash: string;
      if (typeof options.once === 'function') {
        hash = options.once();
      } else {
        hash = reportData.type + reportData.name;
      }
      if (this.reported.has(hash)) {
        return;
      }
      this.reported.add(hash);
    }
    super.report(reportData, options);
  }

  /**
   * Fills out payload arrays with default values according to the given lengths
   * */
  fillPayload(
    reportData: Partial<Pick<TelemetryReportData, 'payload' | 'numberPayload'>>,
    payloadLength: number,
    numberPayloadLength: number
  ): void {
    if (!reportData.payload) {
      reportData.payload = [];
    }
    if (!reportData.numberPayload) {
      reportData.numberPayload = [];
    }
    if (reportData.payload.length < payloadLength) {
      reportData.payload = reportData.payload.concat(Array(payloadLength - reportData.payload.length).fill(''));
    }
    if (reportData.numberPayload.length < numberPayloadLength) {
      reportData.numberPayload = reportData.numberPayload.concat(
        Array(numberPayloadLength - reportData.numberPayload.length).fill(0)
      );
    }
  }

  reportEvent(reportData: CoreTelemetryReportData, options?: CoreTransmissionOptions): void {
    this.fillPayload(reportData, 1, 1);
    this.report(Object.assign({ type: CoreReportType.Event }, reportData), options);
  }

  reportFailure(reportData: CoreTelemetryReportData, options?: CoreTransmissionOptions): void {
    this.fillPayload(reportData, 2, 1);
    this.report(Object.assign({ type: CoreReportType.Failure }, reportData), options);
  }

  reportDuration(reportData: CoreTelemetryReportData, options?: CoreTransmissionOptions): void {
    this.fillPayload(reportData, 1, 2);
    this.report(Object.assign({ type: CoreReportType.Duration }, reportData), options);
  }

  reportSearch(reportData: CoreTelemetryReportData, options?: CoreTransmissionOptions): void {
    this.fillPayload(reportData, 2, 0);
    this.report(Object.assign({ type: CoreReportType.Search }, reportData), options);
  }

  reportTranslation(reportData: CoreTelemetryReportData, options?: CoreTransmissionOptions): void {
    this.fillPayload(reportData, 1, 0);
    this.report(Object.assign({ type: CoreReportType.Translation }, reportData), options);
  }
}
